/**
 * Profile Page Component
 * Displays user profile information and language settings
 * @component ProfilePage
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage, type Language } from '../../contexts/LanguageContext';
import { updateUserLanguage } from '../../services/authService';
import { translate } from '../../locales';
import EditProfileModal from '../../modals/EditProfileModal';
import {
  UkraineFlag,
  USFlag
} from '../../components/Flags';
import './style.css';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  
  // Translation helper
  const t = (key: any) => translate(key, language as 'en' | 'ua');
  
  // Edit profile modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Language state
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);

  // Language dropdown state
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (newLanguage: Language) => {
    if (newLanguage === language) return;

    try {
      setIsUpdatingLanguage(true);
      setLanguageError(null);

      await updateUserLanguage(newLanguage);
      setLanguage(newLanguage);
      setIsLanguageDropdownOpen(false);

      console.log(`🌐 Мову успішно змінено на: ${newLanguage}`);
    } catch (error) {
      console.error('❌ Помилка зміни мови:', error);
      setLanguageError(t('profile.settings.language.error'));
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  // Get display text for language options
  const getLanguageDisplayText = (lang: Language) => {
    const languageName = t(`profile.settings.language.${lang}`);
    return languageName;
  };

  // Get flag component for language
  const getLanguageFlag = (lang: Language) => {
    const isUkrainian = lang === 'ua';
    return isUkrainian ? <UkraineFlag /> : <USFlag />;
  };

  return (
    <div className="profile-page">
      <div className="profile-card">
        {/* Top toolbar with inline language selector (right aligned) */}
        <div className="profile-card-toolbar">
          <div className="language-inline">
            {/* Custom Language Dropdown with Flags */}
            <div className="language-selector" ref={languageDropdownRef}>
              <button
                type="button"
                className="language-button"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                disabled={isUpdatingLanguage}
                aria-label={t('profile.settings.language.title')}
              >
                <span className="language-flag">{getLanguageFlag(language)}</span>
                <span className="language-text">{getLanguageDisplayText(language)}</span>
                <i className={`bi bi-chevron-down ${isLanguageDropdownOpen ? 'open' : ''}`}></i>
              </button>

              {/* Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="language-dropdown">
                  <div className="language-dropdown-list">
                    <button
                      type="button"
                      className={`language-dropdown-item ${language === 'ua' ? 'active' : ''}`}
                      onClick={() => handleLanguageChange('ua')}
                      disabled={isUpdatingLanguage}
                    >
                      <span className="language-flag">{getLanguageFlag('ua')}</span>
                      <span className="language-text">{getLanguageDisplayText('ua')}</span>
                    </button>
                    <button
                      type="button"
                      className={`language-dropdown-item ${language === 'en' ? 'active' : ''}`}
                      onClick={() => handleLanguageChange('en')}
                      disabled={isUpdatingLanguage}
                    >
                      <span className="language-flag">{getLanguageFlag('en')}</span>
                      <span className="language-text">{getLanguageDisplayText('en')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {isUpdatingLanguage && (
              <span className="language-status">
                <i className="bi bi-hourglass-split"></i> {t('profile.settings.language.updating')}
              </span>
            )}
            {languageError && (
              <span className="language-error">
                <i className="bi bi-exclamation-circle"></i> {languageError}
              </span>
            )}
            <button
              type="button"
              className="toolbar-icon-btn"
              onClick={() => setIsEditModalOpen(true)}
              aria-label={t('profile.edit.button')}
              title={t('profile.edit.button')}
            >
              <i className="bi bi-pencil-square"></i>
            </button>
          </div>
        </div>
        {/* <h1>
          <i className="bi bi-person-circle"></i>
          {t('profile.title')}
        </h1> */}
        
        {user ? (
          <>
            {/* User Info */}
            <div className="profile-info">
              <div className="profile-avatar-large">
                <i className="bi bi-person-circle"></i>
              </div>
              <div className="profile-details">
                {/* Повне ім'я - складається з first_name + last_name */}
                <p><strong>{t('profile.fields.name')}:</strong> {user.first_name} {user.last_name}</p>
                
                {/* Email - завжди показуємо */}
                <p>
                  <strong>
                    {t('profile.fields.email')}
                    {user.login_type === 'email' && ' (основний)'}:
                  </strong> 
                  {user.email || 'Не вказано'}
                </p>
                
                {/* Телефон - завжди показуємо */}
                <p>
                  <strong>
                    {t('profile.fields.phone')}
                    {user.login_type === 'phone' && ' (основний)'}:
                  </strong> 
                  {user.phone || 'Не вказано'}
                </p>
                
                {/* Роль */}
                <p><strong>{t('profile.fields.role')}:</strong> {user.role}</p>
                
                {/* Статус верифікації */}
                <p>
                  <strong>Email верифіковано:</strong> 
                  {user.is_verified ? (
                    <span style={{color: 'green'}}> ✓ Так</span>
                  ) : (
                    <span style={{color: 'orange'}}> ⚠ Ні</span>
                  )}
                </p>
                
                <p>
                  <strong>Телефон верифіковано:</strong> 
                  {user.phone_verified ? (
                    <span style={{color: 'green'}}> ✓ Так</span>
                  ) : (
                    <span style={{color: 'orange'}}> ⚠ Ні</span>
                  )}
                </p>
                
                {/* Дати */}
                <p><strong>Зареєстровано:</strong> {new Date(user.created_at).toLocaleDateString('uk-UA')}</p>
                {user.last_login_at && (
                  <p><strong>Останній вхід:</strong> {new Date(user.last_login_at).toLocaleString('uk-UA')}</p>
                )}
              </div>
            </div>
          </>
        ) : (
          <p>{t('profile.loading')}</p>
        )}
      </div>
      
      {/* Edit Profile Modal */}
      <EditProfileModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
